import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import type { TaskAnswerText } from 'shared/modelTypes/client';
import { ShieldAlert } from 'lucide-react';
import { useStore } from 'engine/store';

const openQuestionSchema = z.object({
	answer: z.string().min(1, 'Answer is required'),
});

type OpenQuestionFormData = z.infer<typeof openQuestionSchema>;

export const OpenQuestionForm = ({
	answer,
	readOnly,
	onSubmit,
}: {
	answer?: TaskAnswerText;
	readOnly: boolean;
	onSubmit: (answer: TaskAnswerText) => void;
}) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<OpenQuestionFormData>({
		resolver: zodResolver(openQuestionSchema),
		defaultValues: {
			answer: answer?.text ?? '',
		},
	});

	const state = useStore.getState();
	const isCaptain: boolean = state.teams.find((t) => t.id === state.teamId)?.captainId == state.deviceId;
	const isFrozen = useStore((s) => s.isFrozen);

	return (
		<form
			onSubmit={handleSubmit((data) => {
				if (!readOnly) {
					onSubmit({ kind: 'text', text: data.answer });
				}
			})}
			className="flex flex-col gap-4"
		>
			<div>
				<label htmlFor="answer" className="block text-sm font-medium opacity-90 skin-text">
					{__`Answer`}
				</label>
				<Input
					autoComplete="off"
					readOnly={readOnly}
					id="answer"
					{...register('answer')}
					className="mt-1 w-full"
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault();
						}
					}}
				/>
				{errors.answer && <p className="mt-1 text-sm text-red-600">{errors.answer.message}</p>}
			</div>
			{!answer && readOnly && (
				<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/40">
					<div className="flex w-full flex-col items-center bg-red-500/80 py-2 text-white shadow-lg">
						<ShieldAlert className="mb-1 size-6" />
						<p className="text-sm font-medium">
							{isFrozen
								? __`Game has been frozen by the game master. Submitting answers currently not available.`
								: isCaptain
									? __`Submitting answers not available.`
									: __`Submitting answers not available in spectator mode.`}
						</p>
					</div>
				</div>
			)}
			{!readOnly && (
				<Button className="w-full" type="submit">
					{__`Submit`}
				</Button>
			)}
		</form>
	);
};
